import { useState } from "react";
import { Badge, Callout, Card, Switch, useTheme } from "@promptgatewaysadmin/ui";
import { capabilities } from "../api/capabilities.js";
import { readReduceMotion, writeReduceMotion } from "../auth/persistence.js";
import { OfflineBanner } from "../components/states.js";
import { useSubscriptionState } from "../queries/hooks.js";

/**
 * /settings.
 *
 *   device-local  appearance, reduce motion, keep-signed-in   — a desktop and a laptop can
 *                                                               legitimately differ
 *   account-level push-to-talk key                           — must follow the user to any
 *                                                               machine
 *   always-on     usage metrics, trial & billing reminders   — facts, shown as chips, never
 *                                                               as a toggle we would not honour
 *
 * Me.preferences has not merged, so the push-to-talk row says "set it in the extension". The
 * flag is derived from the generated client surface, so it flips the day the contract lands.
 */
export function Settings() {
  const theme = useTheme();
  const sub = useSubscriptionState();
  const [reduceMotion, setReduceMotion] = useState(readReduceMotion);
  const locked = sub.data?.status === "locked";

  return (
    <div className="pg-page pg-page--narrow">
      <OfflineBanner />

      {locked && (
        <Callout tone="warn" role="status" title="Your trial has ended">
          Settings are read-only until you choose a plan. Nothing was changed.
        </Callout>
      )}

      <Card title="Appearance">
        <Row
          label="Dark mode"
          help="Match the extension's light or dark theme."
          checked={theme.theme === "dark"}
          disabled={locked}
          onChange={() => theme.toggle()}
        />
        <Row
          label="Reduce motion"
          help="Removes animation across the portal and the extension."
          checked={reduceMotion}
          disabled={locked}
          onChange={(v) => {
            setReduceMotion(v);
            writeReduceMotion(v);
          }}
        />
      </Card>

      <Card title="Privacy">
        {/*
          Usage metrics are counts only and always collected — the usage cards are built
          from exactly this data. A toggle that silently does nothing is worse than no
          toggle, so this is a chip stating a fact.
        */}
        <div className="pg-row">
          <div>
            <p className="pg-row__label">Anonymous usage metrics</p>
            <p className="pg-row__help">Counts only — never prompt content.</p>
          </div>
          <Badge tone="neutral">Always on</Badge>
        </div>

        {/*
          Trial and billing reminders are TRANSACTIONAL and always on, so this is a chip
          stating a fact — not a checkbox implying a choice we would not honour. A toggle
          that silently does nothing is worse than no toggle.
        */}
        <div className="pg-row">
          <div>
            <p className="pg-row__label">Trial &amp; billing reminders</p>
            <p className="pg-row__help">
              Always on. These tell you before anything changes about your account.
            </p>
          </div>
          <Badge tone="neutral">Always on</Badge>
        </div>
      </Card>

      <Card title="Shortcuts">
        <div className="pg-row">
          <div>
            <p className="pg-row__label">Push-to-talk key</p>
            <p className="pg-row__help">
              {capabilities.preferences
                ? "Set in the extension; it follows your account."
                : "Set this in the extension for now."}
            </p>
          </div>
          <Badge tone="neutral">In the extension</Badge>
        </div>
      </Card>
    </div>
  );
}

function Row({
  label,
  help,
  checked,
  disabled,
  disabledReason,
  onChange,
}: {
  label: string;
  help: string;
  checked: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onChange: (next: boolean) => void;
}) {
  const id = `row-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className="pg-row">
      <div>
        <p className="pg-row__label" id={`${id}-label`}>
          {label}
        </p>
        <p className="pg-row__help" id={`${id}-help`}>
          {help}
        </p>
        {disabled && disabledReason && (
          <p id={`${id}-reason`} className="pg-disabled-reason">
            {disabledReason}
          </p>
        )}
      </div>
      {/* role="switch" + aria-checked, with a non-colour on/off signal in the component. */}
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        aria-label={label}
        aria-describedby={disabled && disabledReason ? `${id}-reason` : `${id}-help`}
        disabled={disabled}
      />
    </div>
  );
}
